import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { prefersReducedMotion } from '../theme/themeTransition';

let isScrollTriggerRegistered = false;

export function ensureGsapPlugins(): void {
  if (!isScrollTriggerRegistered && typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
    isScrollTriggerRegistered = true;
  }
}

export interface PricingAnimatedValues {
  starterVector: number;
  proVector: number;
  proAi: number;
  studioVector: number;
  studioAi: number;
}

export const PRICING_VALUES_BY_CYCLE: Record<'monthly' | 'yearly', PricingAnimatedValues> = {
  monthly: {
    starterVector: 200,
    proVector: 500,
    proAi: 500,
    studioVector: 1500,
    studioAi: 1500,
  },
  yearly: {
    starterVector: 2400,
    proVector: 6000,
    proAi: 6000,
    studioVector: 18000,
    studioAi: 18000,
  },
};

function getSafePathLength(path: SVGPathElement): number {
  try {
    if (typeof path.getTotalLength === 'function') {
      const length = path.getTotalLength();
      if (Number.isFinite(length) && length > 0) {
        return length;
      }
    }
  } catch {
    // Fallback for environments where SVG geometry is not computed (e.g., jsdom)
  }
  return 160;
}

/**
 * Sets up the Landing Page GSAP choreography (Hero entrance, Capabilities SVG path drawing
 * & card stagger, Pricing cards scroll reveal, and Bottom CTA reveal).
 * Returns a cleanup function that reverts all GSAP contexts and ScrollTriggers.
 */
export function initLandingPageAnimations(rootElement: HTMLElement | null): () => void {
  if (!rootElement || typeof window === 'undefined' || prefersReducedMotion()) {
    return () => {};
  }

  ensureGsapPlugins();

  const ctx = gsap.context(() => {
    // 1. Hero Entrance Timeline
    const heroTitle = rootElement.querySelector<HTMLElement>('.landing-hero-title');
    const heroSubtitle = rootElement.querySelector<HTMLElement>('.landing-hero-subtitle');
    const heroButtons = gsap.utils.toArray<HTMLElement>('.landing-hero-actions .m3-btn', rootElement);
    const heroTrustItems = gsap.utils.toArray<HTMLElement>('.landing-hero-trust > div', rootElement);

    const heroTimeline = gsap.timeline({
      defaults: { ease: 'power3.out' },
    });

    if (heroTitle) {
      heroTimeline.fromTo(
        heroTitle,
        { y: 22, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 0.65, clearProps: 'transform,opacity,visibility' }
      );
    }
    if (heroSubtitle) {
      heroTimeline.fromTo(
        heroSubtitle,
        { y: 16, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 0.55, clearProps: 'transform,opacity,visibility' },
        '-=0.45'
      );
    }
    if (heroButtons.length > 0) {
      heroTimeline.fromTo(
        heroButtons,
        { y: 14, autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.48,
          stagger: 0.08,
          clearProps: 'transform,opacity,visibility',
        },
        '-=0.38'
      );
    }
    if (heroTrustItems.length > 0) {
      heroTimeline.fromTo(
        heroTrustItems,
        { y: 10, autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.42,
          stagger: 0.06,
          clearProps: 'transform,opacity,visibility',
        },
        '-=0.32'
      );
    }

    // 2. Capabilities Architecture Showcase (#features) — SVG Path Drawing & Card Stagger
    const capabilitiesSection = rootElement.querySelector<HTMLElement>('.capabilities-showcase');
    if (capabilitiesSection) {
      const heading = capabilitiesSection.querySelector<HTMLElement>('.capabilities-heading');
      const coreVisual = capabilitiesSection.querySelector<HTMLElement>('.capabilities-core');
      const leftCards = gsap.utils.toArray<HTMLElement>(
        '.capabilities-column-left .capability-card',
        capabilitiesSection
      );
      const rightCards = gsap.utils.toArray<HTMLElement>(
        '.capabilities-column-right .capability-card',
        capabilitiesSection
      );
      const connectorPaths = gsap.utils.toArray<SVGPathElement>(
        '.connector-paths path',
        capabilitiesSection
      );
      const pulsePaths = gsap.utils.toArray<SVGPathElement>(
        '.connector-pulses path',
        capabilitiesSection
      );
      const cardNodes = gsap.utils.toArray<SVGCircleElement>(
        '.connector-node-card',
        capabilitiesSection
      );
      const coreNodes = gsap.utils.toArray<SVGCircleElement>(
        '.connector-node-core',
        capabilitiesSection
      );

      connectorPaths.forEach((path) => {
        const len = getSafePathLength(path);
        gsap.set(path, {
          strokeDasharray: `${len}`,
          strokeDashoffset: len,
        });
      });

      pulsePaths.forEach((path) => {
        const len = getSafePathLength(path);
        const segment = Math.max(24, Math.round(len * 0.22));
        gsap.set(path, {
          strokeDasharray: `${segment} ${len}`,
          strokeDashoffset: len + segment,
          autoAlpha: 0,
        });
      });

      const capabilitiesTl = gsap.timeline({
        scrollTrigger: {
          trigger: capabilitiesSection,
          start: 'top 76%',
          once: true,
        },
      });

      if (heading) {
        capabilitiesTl.fromTo(
          heading,
          { y: 20, autoAlpha: 0 },
          { y: 0, autoAlpha: 1, duration: 0.55, ease: 'power3.out', clearProps: 'transform,opacity,visibility' }
        );
      }

      if (coreVisual) {
        capabilitiesTl.fromTo(
          coreVisual,
          { scale: 0.92, autoAlpha: 0 },
          {
            scale: 1,
            autoAlpha: 1,
            duration: 0.65,
            ease: 'back.out(1.35)',
            clearProps: 'transform,opacity,visibility',
          },
          '-=0.3'
        );
      }

      const interleavedCards: HTMLElement[] = [];
      const maxCardLen = Math.max(leftCards.length, rightCards.length);
      for (let i = 0; i < maxCardLen; i += 1) {
        if (leftCards[i]) interleavedCards.push(leftCards[i]);
        if (rightCards[i]) interleavedCards.push(rightCards[i]);
      }

      if (interleavedCards.length > 0) {
        capabilitiesTl.fromTo(
          interleavedCards,
          { y: 22, autoAlpha: 0 },
          {
            y: 0,
            autoAlpha: 1,
            duration: 0.52,
            stagger: 0.075,
            ease: 'power3.out',
            clearProps: 'transform,opacity,visibility',
          },
          '-=0.45'
        );
      }

      if (cardNodes.length > 0) {
        capabilitiesTl.fromTo(
          cardNodes,
          { scale: 0, autoAlpha: 0, transformOrigin: '50% 50%' },
          {
            scale: 1,
            autoAlpha: 1,
            duration: 0.34,
            stagger: 0.04,
            ease: 'back.out(2)',
          },
          '-=0.42'
        );
      }

      if (connectorPaths.length > 0) {
        capabilitiesTl.to(
          connectorPaths,
          {
            strokeDashoffset: 0,
            duration: 0.78,
            stagger: 0.065,
            ease: 'power2.inOut',
          },
          '-=0.36'
        );
      }

      if (coreNodes.length > 0) {
        capabilitiesTl.fromTo(
          coreNodes,
          { scale: 0, autoAlpha: 0, transformOrigin: '50% 50%' },
          {
            scale: 1,
            autoAlpha: 1,
            duration: 0.36,
            stagger: 0.04,
            ease: 'back.out(2.2)',
          },
          '-=0.32'
        );
      }

      if (pulsePaths.length > 0) {
        capabilitiesTl.to(
          pulsePaths,
          {
            autoAlpha: 0.9,
            duration: 0.25,
          },
          '-=0.1'
        );

        pulsePaths.forEach((path, index) => {
          const len = getSafePathLength(path);
          const segment = Math.max(24, Math.round(len * 0.22));
          gsap.fromTo(
            path,
            { strokeDashoffset: len + segment },
            {
              strokeDashoffset: -segment,
              duration: 2.2 + (index % 3) * 0.35,
              repeat: -1,
              ease: 'none',
              delay: 0.8 + index * 0.22,
            }
          );
        });
      }
    }

    // 3. Pricing Cards Scroll Entrance (#pricing)
    const pricingSection = rootElement.querySelector<HTMLElement>('.pricing-section');
    if (pricingSection) {
      const pricingHeader = pricingSection.querySelector<HTMLElement>('.pricing-header');
      const pricingCards = gsap.utils.toArray<HTMLElement>('.pricing-card', pricingSection);

      const pricingTl = gsap.timeline({
        scrollTrigger: {
          trigger: pricingSection,
          start: 'top 78%',
          once: true,
        },
      });

      if (pricingHeader) {
        pricingTl.fromTo(
          pricingHeader,
          { y: 18, autoAlpha: 0 },
          { y: 0, autoAlpha: 1, duration: 0.5, ease: 'power3.out', clearProps: 'transform,opacity,visibility' }
        );
      }

      if (pricingCards.length > 0) {
        pricingTl.fromTo(
          pricingCards,
          { y: 26, autoAlpha: 0 },
          {
            y: 0,
            autoAlpha: 1,
            duration: 0.56,
            stagger: 0.09,
            ease: 'power3.out',
            clearProps: 'transform,opacity,visibility',
          },
          '-=0.3'
        );
      }
    }

    // 4. Bottom CTA Banner Scroll Reveal
    const bottomCta = rootElement.querySelector<HTMLElement>('.landing-bottom-cta-inner');
    if (bottomCta) {
      gsap.fromTo(
        bottomCta,
        { y: 20, autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.55,
          ease: 'power3.out',
          clearProps: 'transform,opacity,visibility',
          scrollTrigger: {
            trigger: bottomCta,
            start: 'top 84%',
            once: true,
          },
        }
      );
    }
  }, rootElement);

  return () => {
    ctx.revert();
  };
}

/**
 * Tweens credit allowances when the user switches billing cycles.
 *
 * Price text is owned by `useBillingContext`: it can be USD or IDR and must
 * never be overwritten by a USD-only animation frame.
 */
export function animatePricingCycleSwitch(
  rootElement: HTMLElement | null,
  fromCycle: 'monthly' | 'yearly',
  toCycle: 'monthly' | 'yearly'
): gsap.core.Tween | null {
  if (!rootElement || fromCycle === toCycle) return null;

  const targetValues = PRICING_VALUES_BY_CYCLE[toCycle];

  const writeFormattedValues = (values: PricingAnimatedValues) => {
    const setText = (selector: string, text: string) => {
      const el = rootElement.querySelector<HTMLElement>(selector);
      if (el) el.textContent = text;
    };

    setText('[data-gsap-credits="starter-vector"]', Math.round(values.starterVector).toLocaleString('en-US'));
    setText('[data-gsap-credits="pro-vector"]', Math.round(values.proVector).toLocaleString('en-US'));
    setText('[data-gsap-credits="pro-ai"]', Math.round(values.proAi).toLocaleString('en-US'));
    setText('[data-gsap-credits="studio-vector"]', Math.round(values.studioVector).toLocaleString('en-US'));
    setText('[data-gsap-credits="studio-ai"]', Math.round(values.studioAi).toLocaleString('en-US'));
  };

  if (prefersReducedMotion()) {
    writeFormattedValues(targetValues);
    return null;
  }

  const state: PricingAnimatedValues = { ...PRICING_VALUES_BY_CYCLE[fromCycle] };

  const priceEls = gsap.utils.toArray<HTMLElement>('[data-gsap-price]', rootElement);
  if (priceEls.length > 0) {
    gsap.fromTo(
      priceEls,
      { y: -4, autoAlpha: 0.72 },
      { y: 0, autoAlpha: 1, duration: 0.36, stagger: 0.03, ease: 'power2.out', clearProps: 'transform,opacity,visibility' }
    );
  }

  if (toCycle === 'yearly') {
    const saveChip = rootElement.querySelector<HTMLElement>('.pricing-save-chip');
    if (saveChip) {
      gsap.fromTo(
        saveChip,
        { scale: 0.88 },
        { scale: 1, duration: 0.38, ease: 'back.out(2.2)', clearProps: 'transform' }
      );
    }
  }

  return gsap.to(state, {
    ...targetValues,
    duration: 0.42,
    ease: 'power2.out',
    onUpdate: () => {
      writeFormattedValues(state);
    },
    onComplete: () => {
      writeFormattedValues(targetValues);
    },
  });
}

